import { useState, useCallback, useContext } from 'react';
import { formatDate } from 'helpers/utils/dates';
import useReviews from 'frontastic/hooks/useReviews';
import { Option } from 'components/commercetools-ui/atoms/dropdown';
import { AccountContext } from 'context/account';
import type { RatingStatsProps } from 'components/commercetools-ui/molecules/rating-stats';
import type { ReviewListProps } from 'components/commercetools-ui/organisms/review-list';
import type { ReviewFormProps } from 'components/commercetools-ui/organisms/review-form';
import { useRouter } from 'next/navigation';
import { OrderBy, SortBy } from 'shared/types/query/ReviewQuery';
import { ReviewSortOption } from '../../types';
import { UseProductReviewsProps } from './types';

/**
 * Custom hook to handle all product review functionality
 */
const useProductReviews = ({
  productId,
  reviewRatingStatistics,
  configData,
  loginPath = '/login',
}: UseProductReviewsProps) => {
  const router = useRouter();
  const { loggedIn } = useContext(AccountContext);
  const [isReviewDrawerOpen, setIsReviewDrawerOpen] = useState(false);
  const [currentReviewsSortOption, setCurrentReviewsSortOption] = useState<ReviewSortOption>('most_recent');

  const { ratingsGeneral, ratingsSorting, ratingsLoggedIn, ratingsLoggedOut, ratingsForm } = configData;

  const { reviews, reviewStats, isLoading, fetchReviews, submitReview, loadMore, hasMore } = useReviews(
    productId,
    reviewRatingStatistics,
  );

  // Determine if the product has reviews
  const hasReviews = reviewStats.totalCount > 0;

  // Create rating stats props from the review statistics
  const ratingStatsProps: RatingStatsProps = {
    ratingCounts: [
      { ratingValue: 5, ratingCount: reviewStats.ratingDistribution['5'], totalRatings: reviewStats.totalCount },
      { ratingValue: 4, ratingCount: reviewStats.ratingDistribution['4'], totalRatings: reviewStats.totalCount },
      { ratingValue: 3, ratingCount: reviewStats.ratingDistribution['3'], totalRatings: reviewStats.totalCount },
      { ratingValue: 2, ratingCount: reviewStats.ratingDistribution['2'], totalRatings: reviewStats.totalCount },
      { ratingValue: 1, ratingCount: reviewStats.ratingDistribution['1'], totalRatings: reviewStats.totalCount },
    ],
    averageRating: reviewStats.averageRating,
    averageRatingLabel: ratingsGeneral.averageRatingLabel,
    countLabel: ratingsGeneral.reviewsCountLabel?.replace('{ratings}', reviewStats.totalCount.toString()),
    countLabelLink: undefined,
  };

  /**
   * Handle sorting of reviews
   */
  const handleSortReviews = useCallback(
    (value: Option) => {
      if (!productId) return;
      const sortValue = value.value as ReviewSortOption;
      setCurrentReviewsSortOption(sortValue);

      let sortBy: SortBy = 'createdAt';
      let orderBy: OrderBy = 'desc';

      if (sortValue === 'highest_rated') {
        sortBy = 'rating';
        orderBy = 'desc';
      } else if (sortValue === 'lowest_rated') {
        sortBy = 'rating';
        orderBy = 'asc';
      }

      fetchReviews(productId, sortBy, orderBy);
    },
    [productId, fetchReviews],
  );

  /**
   * Handle banner button click based on login state
   */
  const handleBannerClick = useCallback(() => {
    if (loggedIn) {
      setIsReviewDrawerOpen(true);
    } else {
      router.push(loginPath);
    }
  }, [loggedIn, router, loginPath]);

  /**
   * Handle drawer close
   */
  const handleDrawerClose = useCallback(() => {
    setIsReviewDrawerOpen(false);
  }, []);

  /**
   * Handle review form submission
   */
  const handleSubmitReview = async (reviewData: {
    rating: number;
    title: string;
    review: string;
    displayName: string;
  }) => {
    const success = await submitReview(reviewData);
    return success;
  };

  // Get terms and conditions data
  const termsAndConditionsData = ratingsForm.termsAndConditions?.[0] || {};
  const termsAndConditionsProps = {
    mainText: termsAndConditionsData.mainText,
    termsLabel: termsAndConditionsData.termsLabel,
    termsUrl: ratingsForm.termsOfUseLink,
    andText: termsAndConditionsData.andText,
    privacyLabel: termsAndConditionsData.privacyLabel,
    privacyUrl: ratingsForm.privacyNoticeLink,
    checkbox: termsAndConditionsData.checkbox,
  };

  // Review form configuration
  const reviewFormProps: ReviewFormProps = {
    onSubmit: handleSubmitReview,
    selectorLabel: ratingsForm.overallRatingLabel?.[0],
    reviewTitleLabel: ratingsForm.reviewTitleLabel?.[0],
    reviewLabel: ratingsForm.reviewTextLabel?.[0],
    displayNameLabel: ratingsForm.displayNameLabel?.[0],
    submitButtonLabel: ratingsForm.submitReviewButtonLabel,
    termsOfUseLink: ratingsForm.termsOfUseLink as string,
    privacyNoticeLink: ratingsForm.privacyNoticeLink as string,
    reviewConfirmation: {
      title: ratingsForm.reviewConfirmationTitle,
      description: ratingsForm.reviewConfirmationHelperText,
    },
    termsAndConditions: {
      ...termsAndConditionsProps,
      termsUrl: termsAndConditionsProps.termsUrl as string,
      privacyUrl: termsAndConditionsProps.privacyUrl as string,
    },
  };

  // Create review list props with actual review data
  const reviewListProps: ReviewListProps = {
    reviews: reviews.map((review) => ({
      id: review.id,
      title: review.title,
      createdAt: formatDate(review.createdAt),
      rating: review.rating,
      description: review.text,
      author: review.authorName,
      reviewHeading: 'h4',
    })),
    sortOptions: [
      { name: ratingsSorting.mostRecentLabel || 'Most Recent', value: 'most_recent' },
      { name: ratingsSorting.highestRatedLabel || 'Highest Rated', value: 'highest_rated' },
      { name: ratingsSorting.lowestRatedLabel || 'Lowest Rated', value: 'lowest_rated' },
    ],
    sort: handleSortReviews,
    loadMore: hasMore ? loadMore : undefined,
    isLoading: isLoading,
    totalItems: reviewStats.totalCount,
    currentSortValue: currentReviewsSortOption,
  };

  // Determine banner content based on login state and whether there are reviews
  let bannerTitle, bannerBody, bannerButtonLabel, emptyBannerTitle, emptyBannerBody;

  if (!loggedIn) {
    // Not logged in (regardless of reviews)
    bannerTitle = ratingsLoggedOut.reviewsTitle;
    bannerBody = ratingsLoggedOut.reviewsBody;
    bannerButtonLabel = ratingsLoggedOut.buttonLabel;
    emptyBannerTitle = ratingsLoggedOut.noReviewsTitle;
    emptyBannerBody = ratingsLoggedOut.reviewsBody;
  } else if (hasReviews) {
    // Logged in with reviews
    bannerTitle = ratingsLoggedIn.reviewsTitle;
    bannerBody = ratingsLoggedIn.reviewsBody;
    bannerButtonLabel = ratingsLoggedIn.buttonLabel;
    emptyBannerTitle = ratingsLoggedIn.noReviewsTitle;
    emptyBannerBody = ratingsLoggedIn.reviewsBody;
  } else {
    // Logged in without reviews
    bannerTitle = undefined;
    bannerBody = ratingsLoggedIn.reviewsBody;
    bannerButtonLabel = ratingsLoggedIn.buttonLabel;
    emptyBannerTitle = ratingsLoggedIn.noReviewsTitle;
    emptyBannerBody = ratingsLoggedIn.reviewsBody;
  }

  return {
    hasReviews,
    isReviewDrawerOpen,
    ratingStatsProps,
    reviewListProps,
    reviewFormProps,
    bannerTitle,
    bannerBody,
    bannerButtonLabel,
    emptyBannerTitle,
    emptyBannerBody,
    handleBannerClick,
    handleDrawerClose,
  };
};

export default useProductReviews;
